import {
  etapaQueFirma,
  formatearRevision,
  NOMBRE_ESTADO,
  NOMBRE_TIPO_CORRECCION,
  TIPOS_DE_CORRECCION,
} from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { textoBotonFirma } from "../componentes/firmas.ts";
import { HojaConMarcas } from "../componentes/HojaConMarcas.tsx";
import { ModalDevolver, ModalFirma } from "../componentes/ModalFirma.tsx";
import { COLOR_TIPO, type FiltroDeMarcas, PanelDeMarcas } from "../componentes/PanelDeMarcas.tsx";
import { Aviso, Boton, BotonEnlace, EstadoVacio } from "../componentes/ui.tsx";
import { Visor } from "../componentes/Visor.tsx";
import type { Detalle, RevisionDetalle } from "../tipos.ts";

/**
 * Fase 4 · pantalla 09, "Revisar y corregir": quien revisa (o aprueba) anota sobre el documento
 * en vez de escribir un correo aparte. Selecciona una frase en la hoja, elige el tipo (corregir,
 * sugerencia, comentario) y escribe qué hay que cambiar; la marca queda numerada sobre el texto y
 * en el panel. Desde aquí devuelve con correcciones (la misma revisión vuelve a borrador con las
 * marcas vivas) o firma. Quien elaboró ve la misma pantalla para resolverlas y responder.
 *
 * En un documento que es un archivo no hay texto donde anclar: el panel admite comentarios
 * generales, y el visor es el PDF.
 */
export function PaginaRevisar() {
  const { codigo } = useParams({ from: "/app/documentos/$codigo/revisar" });
  const trpc = useTRPC();
  const detalle = useQuery(trpc.documentos.detalle.queryOptions({ codigo }));
  if (detalle.isPending) return <EstadoVacio>Cargando…</EstadoVacio>;
  if (detalle.isError) return <EstadoVacio>{detalle.error.message}</EstadoVacio>;
  const d = detalle.data;
  const principal = d.revisiones.find((r) => r.id === d.principalId) ?? null;
  if (!principal)
    return <EstadoVacio>Este documento no tiene una revisión para revisar.</EstadoVacio>;
  return <Revision d={d} rev={principal} />;
}

function Revision({ d, rev }: { d: Detalle; rev: RevisionDetalle }) {
  const trpc = useTRPC();
  const marcasQ = useQuery(trpc.correcciones.listar.queryOptions({ revisionId: rev.id }));
  const [activa, setActiva] = useState<number | null>(null);
  const [filtro, setFiltro] = useState<FiltroDeMarcas>("todas");
  const [aviso, setAviso] = useState<string | null>(null);
  const [modal, setModal] = useState<"firmar" | "devolver" | null>(null);
  const etapa = etapaQueFirma(rev.estado);
  const vigente = d.revisiones.find((r) => r.id === d.revisionVigenteId) ?? null;

  if (marcasQ.isPending) return <EstadoVacio>Cargando las marcas…</EstadoVacio>;
  if (marcasQ.isError) return <EstadoVacio>{marcasQ.error.message}</EstadoVacio>;
  const m = marcasQ.data;
  const resueltas = m.marcas.filter((x) => x.estado === "resuelta").length;
  const visibles = m.marcas.filter((x) =>
    filtro === "todas"
      ? true
      : filtro === "abiertas"
        ? x.estado === "abierta"
        : x.estado === "resuelta",
  );

  return (
    <div className="max-w-[1320px] pb-10">
      <Encabezado
        overline={`${d.documento.codigo} · Rev. ${formatearRevision(rev.numero)} · ${NOMBRE_ESTADO[rev.estado]}`}
        titulo={`Revisar: ${d.documento.nombre}`}
        acciones={
          <>
            <span className="self-center text-[12.5px] text-texto-3">
              {m.marcas.length} marca{m.marcas.length === 1 ? "" : "s"} · {resueltas} resuelta
              {resueltas === 1 ? "" : "s"}
            </span>
            <BotonEnlace to="/documentos/$codigo" params={{ codigo: d.documento.codigo }}>
              Ver documento
            </BotonEnlace>
            {m.esAutor && rev.redactada && d.permisos.editarContenido && (
              <BotonEnlace
                to="/documentos/$codigo/editar"
                params={{ codigo: d.documento.codigo }}
                variante="primario"
              >
                Corregir en el editor
              </BotonEnlace>
            )}
            {d.permisos.devolver && (
              <Boton variante="peligro" onClick={() => setModal("devolver")}>
                Devolver con correcciones
              </Boton>
            )}
            {d.permisos.firmar && etapa && rev.hashContenido && (
              <Boton variante="firma" onClick={() => setModal("firmar")}>
                {textoBotonFirma(etapa, rev)}
              </Boton>
            )}
          </>
        }
      />
      {aviso && <Aviso className="mb-4">{aviso}</Aviso>}

      <div className="grid gap-5 lg:grid-cols-[1fr_380px] [&>*]:min-w-0">
        <div>
          <LeyendaDeMarcas puedeMarcar={m.puedeMarcar && m.seAncla} />
          {rev.redactada ? (
            <HojaConMarcas
              revisionId={rev.id}
              marcas={visibles}
              activa={activa}
              puedeMarcar={m.puedeMarcar}
              siguienteNumero={Math.max(0, ...m.marcas.map((x) => x.numero)) + 1}
              onElegir={setActiva}
              onAviso={setAviso}
            />
          ) : (
            <Visor revision={rev} informativo={false} fallo={null} />
          )}
        </div>
        <aside className="lg:sticky lg:top-2 lg:max-h-[calc(100vh-1rem)] lg:overflow-y-auto">
          <PanelDeMarcas
            revisionId={rev.id}
            datos={m}
            visibles={visibles}
            filtro={filtro}
            onFiltro={setFiltro}
            activa={activa}
            onElegir={setActiva}
            onAviso={setAviso}
          />
        </aside>
      </div>

      {modal === "devolver" && (
        <ModalDevolver
          revisionId={rev.id}
          numero={rev.numero}
          marcasAbiertas={m.marcas.filter((x) => x.estado === "abierta").length}
          onCerrar={() => setModal(null)}
          onDevuelta={() => setModal(null)}
        />
      )}
      {modal === "firmar" && etapa && rev.hashContenido && (
        <ModalFirma
          revision={rev}
          hashContenido={rev.hashContenido}
          vigente={vigente}
          etapa={etapa}
          onCerrar={() => setModal(null)}
          onFirmado={() => {
            setModal(null);
            queryClient.invalidateQueries();
          }}
        />
      )}
    </div>
  );
}

function LeyendaDeMarcas({ puedeMarcar }: { puedeMarcar: boolean }) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-texto-3">
      {TIPOS_DE_CORRECCION.map((t) => (
        <span key={t} className="flex items-center gap-1.5">
          <span className={`inline-block h-2.5 w-2.5 rounded-sm ${COLOR_TIPO[t]}`} />
          {NOMBRE_TIPO_CORRECCION[t]}
        </span>
      ))}
      {puedeMarcar && (
        <span className="ml-auto text-texto-2">
          Para marcar: selecciona una frase y elige el tipo en el globo que aparece junto a ella.
        </span>
      )}
    </div>
  );
}
